import { RetranslateButton } from 'articleEditor/components/retranslateButton/retranslateButton';
import { RetranslateLoading } from 'articleEditor/components/retranslateButton/retranslateLoading';
import { ArticleDataContext } from 'articleEditor/contexts/articleDataProvider';
import { ArticleSavingContext } from 'articleEditor/contexts/savingContext';
import { TranslationDataContext } from 'articleEditor/contexts/translationProvider';
import { translatingStatusAtom } from 'articleEditor/hooks/translations/useBaseTranslation';
import { useIsTranslating } from 'articleEditor/hooks/translations/useIsTranslating';
import { useTranslateTitle } from 'articleEditor/hooks/translations/useTranslateTitle';
import { useDefaultTranslation } from 'articleEditor/hooks/useDefaultTranslation';
import cx from 'classnames';
import { useAtomValue } from 'jotai';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { TranslatingType } from 'lib-knowledge-remote-types/general.types';
import { ampli } from 'lib-shared-tracking';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';

import s from './title.module.scss';

export function Title(): JSX.Element {
  const { savingStore, actions } = useContext(ArticleSavingContext);
  const {
    currentArticleDetails: { title },
  } = useSnapshot(savingStore);

  const { currentLanguage, defaultLanguage } = useContext(LanguageContext);
  const lastTitleValue = useRef('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const articleData = useContext(ArticleDataContext);
  const isSyncedWithElevio = articleData?.isSyncedWithElevio || false;
  const articleTranslation = useContext(TranslationDataContext);
  const defaultTranslation = useDefaultTranslation();
  const translateTitle = useTranslateTitle();
  const isTranslating = useIsTranslating();
  const translatingStatus = useAtomValue(translatingStatusAtom);
  useEffect(() => {
    // Resize the textarea to fit the text
    // 1px Allows scrollHeight to be calculated
    textareaRef.current!.style.height = '1px';
    textareaRef.current!.style.height = `${
      textareaRef.current!.scrollHeight
    }px`;
  }, [textareaRef, title]);

  // Update the textarea value when the title changes, without losing the cursor position
  useEffect(() => {
    if (lastTitleValue.current !== title) {
      lastTitleValue.current = title;
      textareaRef.current!.value = title;
      textareaRef.current!.style.height = '1px';
      textareaRef.current!.style.height = `${
        textareaRef.current!.scrollHeight
      }px`;
    }
  }, [title]);

  const onInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    lastTitleValue.current = e.currentTarget.value;
    actions.setTitle(e.currentTarget.value);
  };
  const titleClassName = cx(s.title, {
    [s.titleLocked]: isSyncedWithElevio,
  });
  const defaultTranslationTitleUpdatedAt = useMemo(
    () =>
      defaultTranslation?.draft.titleUpdatedAt
        ? Date.parse(defaultTranslation.draft.titleUpdatedAt)
        : undefined,
    [defaultTranslation?.draft.titleUpdatedAt],
  );
  const articleTranslationTitleTranslatedAt = useMemo(
    () =>
      articleTranslation?.draft.titleTranslatedAt
        ? Date.parse(articleTranslation.draft.titleTranslatedAt)
        : undefined,
    [articleTranslation?.draft.titleTranslatedAt],
  );
  const needsRetranslation =
    currentLanguage !== defaultLanguage.languageId &&
    defaultTranslationTitleUpdatedAt &&
    articleTranslationTitleTranslatedAt &&
    defaultTranslationTitleUpdatedAt > articleTranslationTitleTranslatedAt;

  return (
    <div className={s.titleContainer}>
      <textarea
        maxLength={255}
        ref={textareaRef}
        className={titleClassName}
        placeholder="Add a title..."
        onChange={onInput}
        disabled={isSyncedWithElevio || isTranslating}
        data-testid="article-title"
      ></textarea>
      {needsRetranslation && !isTranslating && (
        <RetranslateButton
          translationTooltip="The title of this article has been modified in the default language since it was last translated. Click here to translate it again."
          translateFunction={() => {
            currentLanguage &&
              ampli.knowledgeRetranslateArticleTitleClicked({
                languageId: currentLanguage,
              });
            translateTitle();
          }}
        />
      )}
      {translatingStatus.type === TranslatingType.TITLE && (
        <RetranslateLoading />
      )}
    </div>
  );
}
